import { useMemo } from 'react';
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid 
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useMetricHistory } from '@/hooks/useMetricHistory';
import { formatUSD, formatCompact } from '@/lib/format';
import { format } from 'date-fns';

interface TvlHistoryChartProps {
  slug: string;
  days?: number;
  height?: number;
  showCoverage?: boolean;
}

export function TvlHistoryChart({ 
  slug, 
  days = 90, 
  height = 200,
  showCoverage = true 
}: TvlHistoryChartProps) {
  const { data, isLoading, error } = useMetricHistory(slug, days);

  const chartData = useMemo(() => {
    if (!data?.tvl || data.tvl.length === 0) return [];

    return data.tvl
      .sort((a, b) => a.ts - b.ts)
      .map(point => ({
        date: point.ts,
        tvl: point.value,
        formattedDate: format(new Date(point.ts), 'MMM dd'),
      }));
  }, [data]);

  const stats = useMemo(() => {
    if (chartData.length < 2) return null;

    const first = chartData[0].tvl;
    const last = chartData[chartData.length - 1].tvl;
    const change = ((last - first) / first) * 100;
    const min = Math.min(...chartData.map(d => d.tvl));
    const max = Math.max(...chartData.map(d => d.tvl));

    return { first, last, change, min, max };
  }, [chartData]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className={`w-full`} style={{ height }} />
      </div>
    );
  }

  if (error || !data || chartData.length === 0) {
    return (
      <div 
        className="flex items-center justify-center text-muted-foreground text-sm"
        style={{ height }}
      >
        No TVL history available
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">TVL History</span>
          {stats && (
            <Badge 
              variant={stats.change >= 0 ? "default" : "destructive"}
              className="text-xs"
            >
              {stats.change >= 0 ? '+' : ''}{stats.change.toFixed(1)}%
            </Badge>
          )}
        </div>
        {showCoverage && data.coverage && (
          <span className="text-xs text-muted-foreground">
            {data.coverage.tvl}% coverage ({data.coverage.records} pts)
          </span>
        )}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <defs>
            <linearGradient id="tvlGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="hsl(var(--border))" 
            opacity={0.3}
            vertical={false}
          />
          <XAxis 
            dataKey="formattedDate" 
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis 
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            tickFormatter={(value) => formatCompact(value)}
            tickLine={false}
            axisLine={false}
            width={50}
          />
          <Tooltip 
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const data = payload[0].payload;
              return (
                <div className="bg-popover border border-border rounded-lg p-2 shadow-lg">
                  <p className="text-xs text-muted-foreground">{data.formattedDate}</p>
                  <p className="text-sm font-semibold">{formatUSD(data.tvl)}</p>
                </div>
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="tvl"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            fill="url(#tvlGradient)"
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Min/Max stats */}
      {stats && (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Min: {formatUSD(stats.min)}</span>
          <span>Max: {formatUSD(stats.max)}</span>
        </div>
      )}
    </div>
  );
}
